import { compact, isEmpty, isNumber } from 'lodash';

import { defaultRecordsPerPage, SortDirection } from '@app/constants';

import {
  allRecordsCql,
  SearchOption,
  SearchState,
  SortField,
  sortFieldMap,
} from '../../constants';
import { getQueryCQL } from '../getQueryCQL';
import { getPublicationYearCQL } from '../getPublicationYearCQL';
import { getFacetsCQL } from '../getFacetsCQL';

export interface SQLState extends SearchState {
  hasRequestedFacets?: boolean;
  isConsortiaMode?: boolean;
}

const getCQLSearchQuery = ({
  query = '',
  option = SearchOption.keyword,
  sortField = SortField.relevance,
  sortDirection = SortDirection.asc,
  facets,
  publicationYear,
  hasRequestedFacets,
  isConsortiaMode,
}: SQLState) => {
  if (
    query === '' &&
    !hasRequestedFacets &&
    isEmpty(facets) &&
    isEmpty(publicationYear)
  ) {
    return '';
  }

  const sortByParam =
    sortField !== SortField.relevance
      ? ` sortBy ${sortFieldMap[sortField]}/sort.${sortDirection}`
      : '';

  const isRequestForFacetAttributes =
    hasRequestedFacets &&
    query === '' &&
    isEmpty(facets) &&
    isEmpty(publicationYear);

  if (isRequestForFacetAttributes) {
    return allRecordsCql;
  }

  const facetCQL = getFacetsCQL(facets);
  const publicationYearCQL = getPublicationYearCQL(publicationYear);
  const queryCQL = getQueryCQL({ query, option, isConsortiaMode });
  const queryParamWithFacetParam = compact([
    queryCQL,
    facetCQL,
    publicationYearCQL,
  ]).join(' and ');

  return `${queryParamWithFacetParam}${sortByParam}`;
};

interface SQLQuery {
  query: string;
  limit: number;
  offset?: number;
}

export const getCQLState = ({
  query,
  option,
  sortField,
  sortDirection,
  facets,
  publicationYear,
  pageNumber,
  hasRequestedFacets,
  recordsPerPage = defaultRecordsPerPage,
  limit = recordsPerPage,
  offset,
  isConsortiaMode,
}: SQLState): SQLQuery => {
  const offsetValue =
    offset ??
    (isNumber(pageNumber) &&
      pageNumber > 1 &&
      recordsPerPage * (pageNumber - 1));

  return {
    query: getCQLSearchQuery({
      query,
      option,
      sortField,
      sortDirection,
      facets,
      publicationYear,
      hasRequestedFacets,
      isConsortiaMode,
    }),
    limit,
    ...(offsetValue && {
      offset: offsetValue,
    }),
  };
};
